/**
 * @copyright Copyright 2025 Epic Systems Corporation
 * @file Facilitated App Agreement Button Showroom
 * @module Epic.AppOrchard.Core.FacilitatedAppAgreementButton
 */

import { As, Box, Button, Flex, GridItem, Heading, Text } from "@chakra-ui/react";
import { useAsync } from "@epic/react-async-hook";
import { AgreementModal, AOLink, StatusModal } from "ao/components/Core";
import { requestFacilitatedAppAgreement } from "ao/components/Showroom/data/data";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { getBaseUrl, isLocalhost } from "ao/utils/helpers";
import { isInFlight } from "ao/utils/useAsyncHelpers";
import { FC, memo, useCallback, useMemo, useState } from "react";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	headerColor: string;
	buttonBackgroundColor: string;
	buttonBorderColor: string;
	reload: () => void;
	buttonsAs?: As;
}

export const FacilitatedAppAgreementButton: FC<IProps> = memo((props: IProps) => {
	//#region state and functions

	const { appDetails, headerColor, buttonBackgroundColor, buttonBorderColor, reload, buttonsAs } = props;

	const [showAppTermsModal, setShowAppTermsModal] = useState<boolean>(false);
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<string>("");

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);
	const isShowroomPublic = !isLocalhost() && !siteInfo.isAdminSite;
	const baseUrl = useMemo(() => (isShowroomPublic ? siteInfo.vendorServicesUrl : getBaseUrl()), [
		isShowroomPublic,
		siteInfo.vendorServicesUrl,
	]);
	const showroomBaseUrl = useMemo(() => getBaseUrl(), []);

	const appModel = appDetails.appModel;
	const vendorName = appModel.orgName ?? "the vendor";

	const appAgreementUrl = isShowroomPublic
		? `${showroomBaseUrl}VendorServices/api/Showroom/AppAgreement?termId=${appModel.agreementId}&download=false`
		: `${baseUrl}Developer/AppAgreement?termId=${appModel.agreementId}`;

	const appAgreementDownloadUrl = isShowroomPublic
		? `${showroomBaseUrl}VendorServices/api/Showroom/AppAgreement?termId=${appModel.agreementId}&download=true`
		: `${baseUrl}Developer/AppAgreement?termId=${appModel.agreementId}&download=true`;

	const handleModalClose = useCallback(() => {
		setShowAppTermsModal(false);
	}, []);

	const [requestingAgreementState, requestFacilitatedAgreement] = useAsync(requestFacilitatedAppAgreement, {
		executeImmediately: false,
		displayName: "requestFacilitatedAgreement",
		onSuccess: () => setShowSuccessModal(true),
		onFailure: (error) => {
			console.log("Error requesting facilitated agreement", error);
			setSaveError("Try again later or contact your Epic representative if this continues to happen.");
		},
	});

	const handleRequestFacilitatedAgreement = useCallback(
		() =>
			setTimeout(() => {
				requestFacilitatedAgreement(appDetails.appId!);
			}, 100),
		[requestFacilitatedAgreement, appDetails.appId],
	);

	const handleUseFacilitatedAgreement = useCallback(() => {
		setShowAppTermsModal(false);
		handleRequestFacilitatedAgreement();
	}, [handleRequestFacilitatedAgreement]);

	const handleUseFacilitatedAgreementClicked = useCallback(() => {
		setShowAppTermsModal(true);
	}, []);

	const isRequesting = isInFlight(requestingAgreementState);

	//#endregion

	const UseFacilitatedAgreementButton = buttonsAs || Button;

	const justify = "center";

	return (
		<>
			<GridItem gridArea="header1">
				{!appDetails.facilitatedAppAgreementRequested && (
					<Heading as="h3" color={headerColor} textAlign={justify} fontFamily={"Jost"}>
						Ready to License?
					</Heading>
				)}
			</GridItem>
			<GridItem gridArea="middle1">
				{!appDetails.facilitatedAppAgreementRequested ? (
					<Text textAlign={justify} fontWeight="300" pt="8px" pb="20px">
						{appModel.name}'s license is available through {siteInfo.galleryTokens.galleryName}.
						Review the terms in the app agreement, and if you choose to license it, download,
						sign, and return the agreement to {vendorName}.
					</Text>
				) : (
					<Text textAlign={justify} fontWeight="300" pt="8px" pb="20px">
						{appModel.name}'s agreement was requested by{" "}
						{appDetails.facilitatedAppAgreementRequestedByUser} from your organization on{" "}
						{appDetails.facilitatedAppAgreementRequestedOn}.
					</Text>
				)}
			</GridItem>

			<GridItem gridArea="button1">
				{!appDetails.facilitatedAppAgreementRequested && (
					<Flex justify={justify}>
						<UseFacilitatedAgreementButton
							onClick={handleUseFacilitatedAgreementClicked}
							disabled={isRequesting}
							isLoading={isRequesting}
							backgroundColor={buttonBackgroundColor}
							border={`solid 1px ${buttonBorderColor}`}
						>
							Use Facilitated App Agreement
						</UseFacilitatedAgreementButton>
					</Flex>
				)}
			</GridItem>

			<AgreementModal
				isOpen={showAppTermsModal}
				header={
					<>
						<Box fontWeight="bold">Application Agreement</Box>
						<Box fontWeight="normal" fontSize="medium" mt="0.5em">
							This app is eligible for license facilitation, and {vendorName} has opted in, so
							their product license is available through {siteInfo.galleryTokens.galleryName}.
						</Box>
					</>
				}
				termsUrl={appAgreementUrl}
				downloadTermsUrl={appAgreementDownloadUrl}
				termsAriaLabel="Application Agreement Terms"
				acceptCaption="Notify Vendor & Download"
				acceptButtonDownloadsAgreement
				onClose={handleModalClose}
				onAgree={handleUseFacilitatedAgreement}
				downloadFileName={`${appModel.name} Application Agreement.pdf`}
				skipSigning
				additionalDetails={
					<>
						<Box fontWeight="bold">
							Notify {vendorName} that you're interested below, or contact and work with them
							directly to license their product.
						</Box>
						<Text mt="0.5em">
							To learn more about license facilitation, including app license terms criteria and
							app eligibility criteria, see{" "}
							<AOLink url={`${baseUrl}Article/Index?docid=LicenseFacilitation`} isExternal>
								License Facilitation Information
							</AOLink>
							.
						</Text>
					</>
				}
			/>
			<StatusModal
				title="Error"
				message={saveError}
				isOpen={!!saveError}
				onClose={() => setSaveError("")}
				status="error"
			/>
			<StatusModal
				title="Success"
				message={
					<>
						<Box>{vendorName} was notified that you're interested in using their agreement.</Box>
						<Box>Sign the agreement and return it to {vendorName} to execute the agreement.</Box>
					</>
				}
				isOpen={showSuccessModal}
				onClose={() => {
					reload();
					setShowSuccessModal(false);
				}}
				status="success"
				closeButtonText="Okay"
			/>
		</>
	);
});
